import {
  appendL,
  each,
  equals,
  equals2,
  filter,
  filterL,
  go,
  map,
  mapL,
  rejectL,
} from "fxjs/es";
import { CommonNS } from "../../../../Common/CommonNS.js";
import { LockingNS } from "../../../../Locking/LockingNS.js";
import { WorkingLayerNS } from "../../../../WorkingLayer/WorkingLayerNS.js";
import { checkAreaEnclosure } from "./checkAreaEnclosure.js";
import { checkAreaIntersection } from "./checkAreaIntersection.js";
export class SingleStickerEditorSelection {
  constructor({
    SVGGraphicsElement,
    svg_el,
    working_layer_el,
    working_layer,
    locking,
  }) {
    this._observer_set = new Set();
    this._selected_els = new Set();
    this._SVGGraphicsElement = SVGGraphicsElement;
    this._svg_el = svg_el;
    this._working_layer_el = working_layer_el;
    this._working_layer = working_layer;
    this._locking = locking;
    locking.subscribe((vo) => this._observeLocking(vo));
  }
  setSelectedEls(els) {
    const old_selected_els = this._selected_els;
    const new_selected_els = new Set(
      go(
        els,
        filterL((el) => this._working_layer.getIsChildGraphicsEl(el)),
        rejectL((el) => this._locking.getIsLocked(el)),
        rejectL((el) => equals2("none")(el.getAttributeNS(null, "display")))
      )
    );
    const selected_els = rejectL((el) => old_selected_els.has(el))(
      new_selected_els
    );
    const unselected_els = rejectL((el) => new_selected_els.has(el))(
      old_selected_els
    );
    this._selected_els = new_selected_els;
    this._notify(this._selected_els);
    CommonNS.UtilNS.tryNoop(() =>
      each((el) =>
        CommonNS.UtilNS.tryNoop(() =>
          el.dispatchEvent(
            new CustomEvent("@mp/sticker-editor/select", {
              bubbles: true,
              cancelable: true,
              composed: false,
              detail: {
                el,
                selected_els: new_selected_els,
              },
            })
          )
        )
      )(selected_els)
    );
    CommonNS.UtilNS.tryNoop(() =>
      each((el) =>
        CommonNS.UtilNS.tryNoop(() =>
          el.dispatchEvent(
            new CustomEvent("@mp/sticker-editor/unselect", {
              bubbles: true,
              cancelable: true,
              composed: false,
              detail: {
                el,
                selected_els: new_selected_els,
              },
            })
          )
        )
      )(unselected_els)
    );
  }
  selectEl(el) {
    if (
      this._selected_els.has(el) ||
      !this._working_layer.getIsChildGraphicsEl(el) ||
      this._locking.getIsLocked(el) ||
      equals2("none")(el.getAttributeNS(null, "display"))
    ) {
      return;
    }
    const new_selected_els = new Set(appendL(el)(this._selected_els));
    this._selected_els = new_selected_els;
    this._notify(this._selected_els);
    CommonNS.UtilNS.tryNoop(() =>
      el.dispatchEvent(
        new CustomEvent("@mp/sticker-editor/select", {
          bubbles: true,
          cancelable: true,
          composed: false,
          detail: {
            el,
            selected_els: new_selected_els,
          },
        })
      )
    );
  }
  unselectEl(el) {
    if (!this._selected_els.has(el)) {
      return;
    }
    const new_selected_els = new Set(rejectL(equals(el))(this._selected_els));
    this._selected_els = new_selected_els;
    this._notify(this._selected_els);
    CommonNS.UtilNS.tryNoop(() =>
      el.dispatchEvent(
        new CustomEvent("@mp/sticker-editor/unselect", {
          bubbles: true,
          cancelable: true,
          composed: false,
          detail: {
            el,
            selected_els: new_selected_els,
          },
        })
      )
    );
  }
  convertToSelectableEl(target) {
    const selectable_el = this._working_layer.convertToGraphicsEl(target);
    return selectable_el == null || this._locking.getIsLocked(selectable_el)
      ? null
      : selectable_el;
  }
  getSelectedEls() {
    return this._selected_els;
  }
  subscribe(observer) {
    if (this._observer_set.has(observer)) {
      throw new Error(`이미 등록된 observer 입니다.`);
    }
    observer(this._selected_els);
    this._observer_set.add(observer);
    return () => {
      this._observer_set.delete(observer);
    };
  }
  selectIn({ point1: { x: x1, y: y1 }, point2: { x: x2, y: y2 } }) {
    const points = map(([x, y]) => ({
      x,
      y,
    }))([
      [x1, y1],
      [x2, y1],
      [x2, y2],
      [x1, y2],
    ]);
    this.setSelectedEls(
      filter((el) => {
        const working_layer_el_ctm_inverse = this._working_layer_el
          .getCTM()
          ?.inverse();
        if (working_layer_el_ctm_inverse == null) {
          return false;
        }
        const el_ctm = el.getCTM();
        if (el_ctm == null) {
          return false;
        }
        const ctm = working_layer_el_ctm_inverse.multiply(el_ctm);
        const bbox = el.getBBox();
        const el_points = go(
          [
            [bbox.x, bbox.y],
            [bbox.x + bbox.width, bbox.y],
            [bbox.x + bbox.width, bbox.y + bbox.height],
            [bbox.x, bbox.y + bbox.height],
          ],
          mapL(CommonNS.UtilNS.createSVGPoint(this._svg_el)),
          map((point) => point.matrixTransform(ctm))
        );
        return (
          checkAreaIntersection({
            points1: points,
            points2: el_points,
          }) ||
          checkAreaEnclosure({
            points1: points,
            points2: el_points,
          })
        );
      })(this._working_layer.getGraphicsEls())
    );
  }
  _notify(selected_els) {
    for (const observer of this._observer_set) {
      CommonNS.UtilNS.tryNoop(() => observer(selected_els));
    }
  }
  _observeLocking({ type, el }) {
    if (equals2(type)("UNLOCK")) {
      return;
    }
    if (!this._selected_els.has(el)) {
      return;
    }
    this.unselectEl(el);
  }
}
